/**
 * Màn hình đầu tiên – Chọn vai trò (Demo/Mockup).
 * Bố cục gọn, ít khoảng trắng; responsive đẹp trên mobile và desktop.
 */
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Person, Assignment, Gavel, AccountBalance, ArrowForward } from '@mui/icons-material'
import { useAppDispatch } from '../hooks/redux'
import { setDemoUser } from '../store/slices/authSlice'
/** Hình cân công lý (docs/pngtree-flat-icon-of-balance-scales-of-justice-vector-png-image_12261817.png) */
import scalesIllustrationUrl from '../assets/justice-scales-bg.png?url'
import { brandColors } from '../theme'
import { BRAND_TITLE_LINE1, BRAND_TITLE_LINE2 } from '../components/Logo.tsx'

const ROLES: Array<{
  key: string
  label: string
  description: string
  icon: React.ReactNode
  path: string
  buttonLabel: string
  /** Màu nhận diện vai trò (icon, tiêu đề, nút, viền hover) */
  accent: string
  accentHover: string
}> = [
  /* Thứ tự hiển thị: trái→phải, trên→dưới — Chánh án, Thẩm phán | Thư ký, Đương sự */
  {
    key: 'chief_judge',
    label: 'Chánh án',
    description: 'Phân công án, báo cáo năng suất, cài đặt và tổng quan đơn vị.',
    icon: <AccountBalance />,
    path: '/workspace',
    buttonLabel: 'Vào với vai trò Chánh án',
    accent: brandColors.secondary,
    accentHover: 'rgb(200, 68, 22)',
  },
  {
    key: 'judge',
    label: 'Thẩm phán',
    description: 'Điều hành phiên, xét xử vụ án, lịch làm việc và ra quyết định.',
    icon: <Gavel />,
    path: '/workspace',
    buttonLabel: 'Vào với vai trò Thẩm phán',
    accent: brandColors.primary,
    accentHover: 'rgb(14, 58, 101)',
  },
  {
    key: 'secretary',
    label: 'Thư ký',
    description: 'Tiếp nhận và xử lý sơ bộ đơn, quản lý hồ sơ, phối hợp Thẩm phán.',
    icon: <Assignment />,
    path: '/workspace',
    buttonLabel: 'Vào với vai trò Thư ký',
    accent: '#7C3AED',
    accentHover: '#6d28d9',
  },
  {
    key: 'user',
    label: 'Đương sự',
    description: 'Theo dõi vụ án, nộp đơn khởi kiện và nhận thông báo tiến trình.',
    icon: <Person />,
    path: '/cases',
    buttonLabel: 'Bắt đầu với tư cách Đương sự',
    accent: '#0D9488',
    accentHover: '#0f766e',
  },
]

export default function RoleSelectPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleSelect = (key: string, path: string) => {
    dispatch(setDemoUser(key))
    navigate(path, { replace: true })
  }

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        pb: { xs: 3, sm: 4 },
        overflow: 'hidden',
        /* Nền xanh nhạt rõ (khác body #f8fafc) — luôn thấy đổi so với trang khác */
        bgcolor: '#c5d9ef',
      }}
    >
      {/* Cân công lý — PNG từ assets (pngtree flat icon) */}
      <Box
        component="img"
        src={scalesIllustrationUrl}
        alt=""
        aria-hidden
        sx={{
          position: 'fixed',
          left: '50%',
          bottom: 0,
          transform: 'translateX(-50%)',
          width: { xs: 'min(100vw, 720px)', sm: 'min(88vw, 920px)' },
          maxHeight: { xs: '50vh', sm: '56vh' },
          height: 'auto',
          objectFit: 'contain',
          objectPosition: 'bottom center',
          zIndex: 0,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />
      {/* Chỉ làm mờ phần trên màn hình — không phủ kín nửa dưới (chỗ cân) */}
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: { xs: '38%', sm: '40%' },
          zIndex: 1,
          background:
            'linear-gradient(180deg, rgba(252, 253, 255, 0.98) 0%, rgba(210, 228, 248, 0.45) 70%, rgba(197, 217, 239, 0) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Nội dung chính: canh từ trên xuống, không căn giữa để tránh dư trắng */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 960,
          mx: 'auto',
          flex: 1,
          px: { xs: 2, sm: 3 },
          pt: { xs: 2.5, sm: 3, md: 4 },
        }}
      >
        <Box sx={{ textAlign: 'center', mb: { xs: 2.5, sm: 3 } }}>
          <Typography
            component="div"
            fontWeight={700}
            sx={{
              color: brandColors.primary,
              fontSize: { xs: '0.78rem', sm: '0.95rem', md: '1.05rem' },
              letterSpacing: '-0.01em',
              lineHeight: 1.35,
              textAlign: 'center',
            }}
          >
            {BRAND_TITLE_LINE1}
          </Typography>
          <Typography
            component="div"
            fontWeight={600}
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.7rem', sm: '0.82rem', md: '0.88rem' },
              mt: 0.5,
              mb: 0.25,
              lineHeight: 1.35,
              textAlign: 'center',
            }}
          >
            {BRAND_TITLE_LINE2}
          </Typography>
        </Box>

        {/* Grid 1 cột mobile, 2 cột tablet+ */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: { xs: 1.5, sm: 2 },
            width: '100%',
          }}
        >
          {ROLES.map((r) => (
            <Card
              key={r.key}
              elevation={0}
              sx={{
                cursor: 'pointer',
                borderRadius: 2,
                border: '2px solid',
                borderColor: alpha(r.accent, 0.22),
                bgcolor: alpha(r.accent, 0.05),
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                minHeight: isMobile ? 0 : 180,
                '&:hover': {
                  boxShadow: `0 12px 28px ${alpha(r.accent, 0.22)}`,
                  transform: 'translateY(-2px)',
                  borderColor: r.accent,
                  bgcolor: alpha(r.accent, 0.08),
                },
                '&:active': { transform: 'scale(0.99)' },
              }}
              onClick={() => handleSelect(r.key, r.path)}
            >
              <CardContent
                sx={{
                  p: { xs: 2, sm: 2.25 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  textAlign: 'left',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minHeight: 0 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      bgcolor: r.accent,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& .MuiSvgIcon-root': { fontSize: 22 },
                    }}
                  >
                    {r.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        color: r.accent,
                        fontSize: { xs: '0.9375rem', sm: '1rem' },
                        mb: 0.25,
                      }}
                    >
                      {r.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {r.description}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  disableElevation
                  endIcon={<ArrowForward sx={{ fontSize: 18 }} />}
                  sx={{
                    mt: 1.5,
                    py: { xs: 1.25, sm: 1.125 },
                    minHeight: 44,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    bgcolor: r.accent,
                    '&:hover': { bgcolor: r.accentHover },
                  }}
                >
                  {r.buttonLabel}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
